import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "@store/middleware";

import { getApplicationSpaBackendUrl } from "@utils";
import { type IDocumentInfo } from "@types";

export const DOCUMENT_INFO_API_REDUCER_KEY = "documentInfoApi";
export const documentInfoApi = createApi({
  reducerPath: DOCUMENT_INFO_API_REDUCER_KEY,
  baseQuery: (arguments_, api, extraOptions) =>
    baseQueryWithAuth({ baseUrl: getApplicationSpaBackendUrl() })(arguments_, api, {
      ...extraOptions,
      isExternalService: true,
    }),
  endpoints: (builder) => ({
    getDocumentInfoById: builder.query<IDocumentInfo, IDocumentInfo["id"]>({
      query: (id) => ({
        url: `/document-info/${id}`,
        method: "GET",
      }),
    }),
    getDocumentInfoByExternalId: builder.query<IDocumentInfo, IDocumentInfo["externalId"]>({
      query: (externalId) => ({
        url: `/document-info/external/${externalId}`,
        method: "GET",
      }),
    }),
    getDocumentInfoBy: builder.query<
      IDocumentInfo[],
      {
        externalId?: IDocumentInfo["externalId"];
        externalDocumentIdentifier?: IDocumentInfo["externalDocumentIdentifier"];
        type?: IDocumentInfo["type"];
        purchaseType?: IDocumentInfo["purchaseType"];
        documentCreatedIn?: IDocumentInfo["documentCreatedIn"];
        bundleId?: IDocumentInfo["bundleId"];
      }
    >({
      query: (where) => {
        const parameters = new URLSearchParams();
        for (const [key, value] of Object.entries(where)) {
          if (value !== undefined && value !== null && value !== "") {
            parameters.append(key, String(value));
          }
        }

        return {
          url: `/document-info?${parameters.toString()}`,
          method: "GET",
        };
      },
    }),
    postDocumentInfo: builder.mutation<
      IDocumentInfo,
      {
        externalId: IDocumentInfo["externalId"];
        externalDocumentIdentifier: IDocumentInfo["externalDocumentIdentifier"];
        type: IDocumentInfo["type"];
        purchaseType: IDocumentInfo["purchaseType"];
        documentCreatedIn: IDocumentInfo["documentCreatedIn"];
        completed: boolean;
      }
    >({
      query: (body) => ({
        url: "/document-info",
        method: "POST",
        body,
      }),
    }),
    patchDocumentInfo: builder.mutation<
      IDocumentInfo,
      {
        id: IDocumentInfo["id"];
        body: {
          externalId?: IDocumentInfo["externalId"];
          externalDocumentIdentifier?: IDocumentInfo["externalDocumentIdentifier"];
          type?: IDocumentInfo["type"];
          purchaseType?: IDocumentInfo["purchaseType"];
          documentCreatedIn?: IDocumentInfo["documentCreatedIn"];
          completed: boolean;
        };
      }
    >({
      query: ({ id, body }) => ({
        url: `/document-info/${id}`,
        method: "PATCH",
        body,
      }),
    }),
    deleteDocumentInfo: builder.mutation({
      query: (id: IDocumentInfo["id"]) => ({
        url: `/document-info/${id}`,
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useGetDocumentInfoByQuery,
  useLazyGetDocumentInfoByIdQuery,
  useLazyGetDocumentInfoByExternalIdQuery,
  useGetDocumentInfoByExternalIdQuery,
  useGetDocumentInfoByIdQuery,
  useLazyGetDocumentInfoByQuery,
  usePostDocumentInfoMutation,
  usePatchDocumentInfoMutation,
  useDeleteDocumentInfoMutation,
} = documentInfoApi;
